import { useCallback, useEffect, useRef } from 'react';
import { aiChatApi } from './aiChat.api';
import type { AiChatMessage } from './aiChat.types';

export function messageAudioUrl(message: AiChatMessage): string | undefined {
  return (
    message.attachments.find(
      (attachment) => attachment.mimeType.startsWith('audio/') && attachment.downloadUrl,
    )?.downloadUrl ?? undefined
  );
}

export function hasMessageAudio(message: AiChatMessage): boolean {
  return message.attachments.some(
    (attachment) => attachment.mimeType.startsWith('audio/') && attachment.uploadComplete,
  );
}

// The server signs attachment links for 900 seconds. Only freshly read or
// generated links receive a shorter local lifetime; unknown links are re-read.
export const SPEECH_URL_CACHE_MS = 14 * 60 * 1000;
type SpeechLink = { url: string; expiresAt: number; attachmentId?: string };

/** Reuses saved speech before requesting TTS. Browser speech is an unconfigured-provider fallback. */
export function useMessageSpeech({
  enabled,
  scopeKey,
  providerConfigured,
  canGenerate,
  voiceOutputEnabled,
  volume,
  speakDevice,
  pauseDeviceListening,
  stopDeviceSpeaking,
  refreshSavedAudio,
}: {
  enabled: boolean;
  scopeKey: string;
  providerConfigured: boolean;
  canGenerate: boolean;
  voiceOutputEnabled: boolean;
  volume: number;
  speakDevice: (text: string, options?: { manual?: boolean }) => boolean;
  pauseDeviceListening: () => (resume?: boolean) => void;
  stopDeviceSpeaking: () => void;
  refreshSavedAudio: (messageId: string, attachmentId?: string) => Promise<string | undefined>;
}) {
  const current = useRef({
    enabled,
    scopeKey,
    providerConfigured,
    canGenerate,
    voiceOutputEnabled,
    volume,
    speakDevice,
    pauseDeviceListening,
    stopDeviceSpeaking,
    refreshSavedAudio,
  });
  current.current = {
    enabled,
    scopeKey,
    providerConfigured,
    canGenerate,
    voiceOutputEnabled,
    volume,
    speakDevice,
    pauseDeviceListening,
    stopDeviceSpeaking,
    refreshSavedAudio,
  };
  const audio = useRef<HTMLAudioElement | null>(null);
  const speechUrls = useRef(new Map<string, Promise<SpeechLink>>());
  const savedSpeech = useRef(new Map<string, string | undefined>());
  const resumeListening = useRef<((resume?: boolean) => void) | null>(null);
  const generation = useRef(0);
  const stopSpeaking = useCallback(() => {
    generation.current += 1;
    if (audio.current) {
      audio.current.onended = null;
      audio.current.onerror = null;
      audio.current.pause();
    }
    audio.current = null;
    resumeListening.current?.(false);
    resumeListening.current = null;
    current.current.stopDeviceSpeaking();
  }, []);

  const speak = useCallback(
    async (
      message: AiChatMessage,
      options: { manual?: boolean; persistedOnly?: boolean } = {},
    ): Promise<void> => {
      const settings = current.current;
      if (!settings.enabled || (!options.manual && !settings.voiceOutputEnabled)) return;
      stopSpeaking();
      const request = generation.current;
      const stillCurrent = () =>
        generation.current === request &&
        current.current.enabled &&
        current.current.scopeKey === settings.scopeKey;
      const cache = speechUrls.current;
      const knownSaved = savedSpeech.current;
      const attachment = message.attachments.find(
        (item) => item.mimeType.startsWith('audio/') && item.uploadComplete,
      );
      if (attachment) knownSaved.set(message.id, attachment.id);
      let pending = cache.get(message.id);
      let refreshed = false;
      const refresh = () => {
        refreshed = true;
        const attachmentId = knownSaved.get(message.id);
        const fresh = settings
          .refreshSavedAudio(message.id, attachmentId)
          .then((url) => {
            if (
              !stillCurrent() &&
              (current.current.scopeKey !== settings.scopeKey || !current.current.enabled)
            ) {
              throw new Error('Speech request is no longer active');
            }
            if (!url)
              throw new Error('Saved speech is unavailable. Refresh the chat and try again.');
            return { url, expiresAt: Date.now() + SPEECH_URL_CACHE_MS, attachmentId };
          })
          .catch(() => {
            throw new Error('Saved speech is unavailable. Refresh the chat and try again.');
          });
        cache.set(message.id, fresh);
        return fresh;
      };
      // A message's existing URL may already be old when first clicked. Re-sign
      // through the authorized read instead of giving that unknown URL a new TTL.
      if (!pending && knownSaved.has(message.id)) pending = refresh();
      // Playing existing audio does not require a configured generation provider
      // or device speech support. Keep fallback limited to replies without audio.
      if (!pending && !settings.providerConfigured) {
        settings.speakDevice(message.content, { manual: options.manual });
        return;
      }
      const release = settings.pauseDeviceListening();
      resumeListening.current = release;
      const finish = () => {
        if (resumeListening.current !== release) return;
        resumeListening.current = null;
        release(stillCurrent());
      };
      try {
        if (!pending) {
          if (options.persistedOnly) {
            throw new Error('OpenAI speech was not returned. Use Speak last reply to try again.');
          }
          if (!settings.canGenerate)
            throw new Error('You do not have permission to generate speech');
          pending = aiChatApi.speakMessage({ messageId: message.id }).then((result) => {
            if (!result.ok || !result.data?.downloadUrl) {
              throw new Error(result.error ?? 'OpenAI speech is unavailable');
            }
            if (current.current.scopeKey !== settings.scopeKey || !current.current.enabled) {
              throw new Error('Speech request is no longer active');
            }
            knownSaved.set(message.id, result.data.id);
            return {
              url: result.data.downloadUrl,
              expiresAt: Date.now() + SPEECH_URL_CACHE_MS,
              attachmentId: result.data.id,
            };
          });
          cache.set(message.id, pending);
        }
        let link = await pending;
        if (!stillCurrent()) return;
        if (link.expiresAt <= Date.now() || (attachment && link.attachmentId !== attachment.id)) {
          if (cache.get(message.id) === pending) cache.delete(message.id);
          pending = cache.get(message.id) ?? refresh();
          link = await pending;
        }
        if (!stillCurrent()) return;
        const play = async (url: string) => {
          const playback = new Audio(url);
          playback.volume = current.current.volume;
          audio.current = playback;
          let started = false;
          playback.onended = () => {
            if (audio.current === playback) {
              audio.current = null;
              finish();
            }
          };
          playback.onerror = () => {
            if (audio.current !== playback) return;
            cache.delete(message.id);
            // A later media failure is refreshed on explicit replay. Never
            // generate speech or loop reads from an asynchronous media event.
            if (started && audio.current === playback) {
              audio.current = null;
              finish();
            }
          };
          try {
            await playback.play();
            started = true;
          } catch {
            playback.onended = null;
            playback.onerror = null;
            playback.pause();
            if (audio.current === playback) audio.current = null;
            throw new Error('Saved speech could not play. Refresh the chat and try again.');
          }
        };
        try {
          await play(link.url);
        } catch {
          if (refreshed || !stillCurrent())
            throw new Error('Saved speech could not play. Refresh the chat and try again.');
          cache.delete(message.id);
          pending = refresh();
          link = await pending;
          if (!stillCurrent()) return;
          await play(link.url);
        }
      } catch (error) {
        if (cache.get(message.id) === pending) cache.delete(message.id);
        finish();
        if (!stillCurrent()) return;
        throw error;
      }
    },
    [stopSpeaking],
  );

  useEffect(() => {
    stopSpeaking();
    speechUrls.current = new Map();
    savedSpeech.current = new Map();
    return stopSpeaking;
  }, [enabled, scopeKey, stopSpeaking]);

  useEffect(() => {
    if (!voiceOutputEnabled) stopSpeaking();
  }, [voiceOutputEnabled, stopSpeaking]);

  useEffect(() => {
    const stopWhenHidden = () => {
      if (document.visibilityState === 'hidden') stopSpeaking();
    };
    document.addEventListener('visibilitychange', stopWhenHidden);
    return () => document.removeEventListener('visibilitychange', stopWhenHidden);
  }, [stopSpeaking]);

  return { speak, stopSpeaking };
}
